<template>
  <v-dialog v-model="showApprovalPopup" persistent scrollable max-width="980" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <div class="fdlg__form">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">{{ cause || "Demers" }}</h2>
            <p class="fdlg__sub">{{ [equipment, model].filter(Boolean).join(" ") }}<template v-if="department">, {{ department }}</template></p>
          </div>
          <v-btn icon aria-label="Închide" @click="showApprovalPopup = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div v-if="isCorrected" class="notice notice--sky">
            <v-icon>mdi-account-edit-outline</v-icon>
            <span>
              <strong>Corectat de utilizator.</strong> Contabilitatea a respins demersul, iar autorul l-a corectat.
              Ce s-a schimbat găsiți în istoricul comentariilor.
            </span>
          </div>

          <div class="facts-grid">
            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-account-outline</v-icon>Cine a cerut</div>
              <dl class="facts">
                <dt>Utilizatorul</dt>
                <dd>{{ user }}</dd>
                <dt>Departament</dt>
                <dd>{{ department }}</dd>
                <dt>Telefon mobil</dt>
                <dd>{{ mobilePhone }}</dd>
                <dt>Telefon fix</dt>
                <dd>{{ fixPhone }}</dd>
              </dl>
            </section>
            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>Echipamentul</div>
              <div v-if="createsEquipment" class="new-item">
                <v-icon small>mdi-new-box</v-icon>Echipament nou (cerut prin demers)
              </div>
              <dl class="facts">
                <dt>Tip</dt>
                <dd>{{ equipment }}</dd>
                <dt>Model</dt>
                <dd>{{ model }}</dd>
                <dt>Nr. inventar</dt>
                <dd>{{ registration || "—" }}</dd>
                <dt>Locația</dt>
                <dd>{{ location }}</dd>
                <dt>Cauza</dt>
                <dd>{{ cause }}</dd>
              </dl>
              <div v-if="canEditItem" class="item-edit">
                <v-btn outlined color="primary" @click="$refs.itemEdit.open()">
                  <v-icon left>mdi-pencil-outline</v-icon>Editează datele echipamentului
                </v-btn>
                <p class="item-edit__hint">Se salvează în istoricul demersului («Date modificate»); demersul nu își schimbă etapa.</p>
              </div>
            </section>
          </div>

          <div class="last-exec" :class="execClass">
            <v-icon>{{ refillAlert ? "mdi-alert-octagon-outline" : "mdi-history" }}</v-icon>
            <div class="last-exec__main">
              <template v-if="execLoading">Se verifică cererile anterioare pe acest echipament...</template>
              <template v-else-if="!execution">
                <strong>Ultima executare:</strong> cererile anterioare nu au putut fi verificate.
              </template>
              <template v-else-if="!hasPrevious">
                <strong>Ultima executare:</strong> {{ execution.message || "Prima cerere pentru acest echipament." }}
              </template>
              <template v-else>
                <div>
                  <strong>Ultima executare:</strong>
                  {{ execution.requestType }}, {{ execution.created_at }}
                  ({{ execution.created_at ? calcTimeDifference(execution.created_at) : "N/A" }}).
                </div>
                <div class="last-exec__count">
                  Înainte de aceasta: <strong>{{ sameCauseText }}</strong> în ultimele 12 luni.
                </div>
                <div v-if="refillAlert" class="last-exec__warn">
                  Verificați dacă cererea este justificată: aceeași cauză se repetă des pe acest echipament
                  (se numără toți utilizatorii și toate transferurile lui).
                </div>
                <button
                  type="button"
                  class="last-exec__toggle"
                  :aria-expanded="showHistory ? 'true' : 'false'"
                  @click="showHistory = !showHistory"
                >
                  <v-icon small>{{ showHistory ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                  <span>{{ showHistory ? "Ascunde" : "Vezi" }} cererile anterioare pe acest echipament ({{ historyCountText }})</span>
                </button>
              </template>
            </div>
          </div>
          <ItemRequestHistory
            v-if="hasPrevious && showHistory"
            class="last-exec__list"
            :items="execution.history || []"
            :same-cause-id="requestTypeId"
          />

<section v-if="details && details.circuit" class="form-section">
            <div class="form-section__label"><v-icon small>mdi-sitemap-outline</v-icon>Traseul aprobării</div>
            <p class="form-section__note">Circuitul „{{ details.circuit.name }}”, cu care a fost depus demersul.</p>
            <CircuitPath
              :steps="details.circuit.steps"
              :review="details.circuit.review"
              :engineer-role="details.engineerRole"
              :waiting="details.waitingFor || ''"
              :finished="statusId === 9"
              :examined="!!details.examined"
            />
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-message-text-outline</v-icon>Istoricul comentariilor</div>
            <p v-if="!commentsHistory || commentsHistory.length === 0" class="form-section__note">Nu există comentarii anterioare.</p>
            <ul v-else class="comments">
              <li v-for="(item, index) in commentsHistory" :key="item.id || index">
                <div class="comments__head">
                  <strong>{{ item.user }}</strong>
                  <StatusChip :status="item.status" small />
                  <span class="comments__date">{{ formatDate(item.date) }}</span>
                </div>
                <div v-if="item.comment" class="comments__text">{{ item.comment }}</div>
                <PhotoGallery v-if="item.files && item.files.length" class="comments__photos" :files="item.files" :size="64" download-name="demers" />
              </li>
            </ul>
            <v-btn v-if="canComment" outlined color="primary" class="comments__add" @click="openAddComment">
              <v-icon left>mdi-comment-plus-outline</v-icon>Adaugă comentariu
            </v-btn>
          </section>

          <section v-if="stepsLoading || steps.length" class="form-section">
            <div class="form-section__label"><v-icon small>mdi-pencil-outline</v-icon>{{ canFinish ? "Îndeplinirea demersului" : "Comentariul dvs." }}</div>
            <v-row v-if="canFinish" dense>
              <v-col cols="12" sm="5" md="4">
                <v-text-field
                  v-model="price"
                  label="Prețul deservirii"
                  prepend-inner-icon="mdi-cash"
                  suffix="lei"
                  hint="Se salvează când marcați demersul ca îndeplinit. De exemplu 150 sau 150,50"
                  persistent-hint
                  :error-messages="priceError"
                  outlined
                  class="mb-2"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-textarea
              v-model="body"
              placeholder="Opțional. Se salvează împreună cu decizia dvs."
              outlined
              auto-grow
              rows="2"
              hide-details
              class="mb-3"
            ></v-textarea>
            <PhotoInput
              v-model="photos"
              purpose="comment"
              :max="6"
              label="Fotografii (opțional)"
              hint="Se salvează împreună cu decizia dvs."
              @uploading="photosUploading = $event"
              @error="$emit('error', $event)"
            />
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <template v-if="stepsLoading">
            <v-progress-circular indeterminate size="20" width="2" color="primary"></v-progress-circular>
            <span class="steps-note">Se încarcă acțiunile...</span>
          </template>
          <template v-else-if="steps.length === 0">
            <v-icon class="steps-note__icon">mdi-information-outline</v-icon>
            <span class="steps-note">{{ noStepsText }}</span>
            <v-spacer></v-spacer>
            <v-btn text large @click="showApprovalPopup = false">Închide</v-btn>
          </template>
          <template v-else>
            <v-btn
              v-for="step in rejectSteps"
              :key="step.id"
              outlined
              large
              color="error"
              :disabled="saving || photosUploading"
              @click="showConfirmCard(step)"
            >
              <v-icon left>{{ stepIcon(step) }}</v-icon>{{ step.label }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-for="step in secondarySteps"
              :key="step.id"
              outlined
              large
              color="primary"
              :disabled="saving || photosUploading"
              @click="showConfirmCard(step)"
            >
              {{ step.label }}
            </v-btn>
            <v-btn
              v-for="step in approveSteps"
              :key="step.id"
              class="btn-green"
              large
              :loading="saving && savingStep === step.id"
              :disabled="(saving && savingStep !== step.id) || photosUploading"
              @click="showConfirmCard(step)"
            >
              <v-icon left>{{ stepIcon(step) }}</v-icon>{{ step.label }}
            </v-btn>
          </template>
        </div>
      </div>
      <ConfirmCard v-if="confirmCard" :title="confirmTitle" @cancel="cancelConfirmCard" @confirm="confirmAction" />
      <ItemEditDialog ref="itemEdit" :request="details" @saved="onItemSaved" />
      <AddCommentDialog ref="addComment" @saved="onCommentAdded" @success="$emit('success', $event)" @error="$emit('error', $event)" />
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import ConfirmCard from "@/components/elements/ConfirmCard.vue";
import StatusChip from "@/components/elements/StatusChip.vue";
import ItemEditDialog from "@/components/requests/ItemEditDialog.vue";
import ItemRequestHistory from "@/components/requests/ItemRequestHistory.vue";
import CircuitPath from "@/components/requests/CircuitPath.vue";
import AddCommentDialog from "@/components/requests/AddCommentDialog.vue";
import PhotoInput from "@/components/files/PhotoInput.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import { mapGetters } from "vuex";
import { ITEM_EDITOR_ROLES, STAGES_005 } from "@/config/periferice";

// What the person sees after each step (by response_types id)
const DONE_MESSAGES = {
  4: "Demersul a fost respins",
  8: "Demersul a fost preluat în lucru",
  9: "Demersul a fost marcat ca îndeplinit",
  10: "Demersul a fost marcat „Imposibil la moment”",
  16: "Demersul a fost trimis pentru examinare",
};

const STEP_ICONS = {
  4: "mdi-close-circle-outline",
  8: "mdi-progress-wrench",
  10: "mdi-pause-circle-outline",
};

const DONE = 9;

// From this many earlier requests with the same cause in 12 months the refill check turns red
const REFILL_ALERT = 3;

// "1 cerere", "5 cereri", "20 de cereri"
function requestsCount(n) {
  if (n === 1) return "1 cerere";
  const rest = n % 100;
  return `${n} ${n >= 20 && (rest === 0 || rest >= 20) ? "de " : ""}cereri`;
}

export default {
  props: {
    request: Object,
  },
  components: {
    ConfirmCard,
    StatusChip,
    ItemEditDialog,
    ItemRequestHistory,
    CircuitPath,
    AddCommentDialog,
    PhotoInput,
    PhotoGallery,
  },
  data() {
    return {
      registration: "",
      showApprovalPopup: false,
      user: "",
      equipment: "",
      model: "",
      department: "",
      body: "",
      // photos of the decision (already uploaded, linked when the step is saved)
      photos: [],
      photosUploading: false,
      location: "",
      cause: "",
      mobilePhone: "",
      fixPhone: "",
      commentsHistory: [],
      // the buttons come from the server: [{ id, label, tone }]
      steps: [],
      // what the work cost, saved with "Îndeplinit"
      price: "",
      stepsLoading: false,
      stepsFailed: false,
      details: null,
      execution: null,
      execLoading: false,
      showHistory: false,
      confirmCard: false,
      confirmTitle: "",
      pendingStep: null,
      saving: false,
      savingStep: null,
      // a slower answer for a request opened earlier must not overwrite the one on screen
      loadToken: 0,
    };
  },
  methods: {
    show() {
      this.showApprovalPopup = true;
      helper.scrollToTop(this);
    },
    async loadData() {
      const request = this.request;
      if (!request) return;
      const token = ++this.loadToken;
      const requestId = request.requestId;

      this.steps = [];
      this.stepsLoading = true;
      this.stepsFailed = false;
      this.details = null;
      this.execution = null;
      this.execLoading = true;
      this.showHistory = false;
      this.commentsHistory = [];
      this.mobilePhone = request.telephone && request.telephone.length > 0 ? request.telephone : "Nu există";
      this.fixPhone = request.landline && request.landline.length > 0 ? request.landline : "Nu există";

      const [steps, details, execution, comments] = await Promise.allSettled([
        api.getNextSteps(requestId),
        api.getRequestDetails(requestId),
        api.getLastExecution(request.userId, request.equipmentId, requestId),
        api.getRequestCommentsHistory(requestId),
      ]);
      if (token !== this.loadToken) return;

      this.stepsLoading = false;
      if (steps.status === "fulfilled") {
        this.steps = steps.value || [];
      } else {
        console.error("Error fetching next steps:", steps.reason);
        this.stepsFailed = true;
      }
      if (details.status === "fulfilled" && details.value) {
        this.applyDetails(details.value);
      }
      this.execLoading = false;
      if (execution.status === "fulfilled") {
        this.execution = execution.value;
      } else {
        console.error("Error fetching last execution:", execution.reason);
      }
      if (comments.status === "fulfilled") {
        this.commentsHistory = comments.value;
      } else {
        console.error("Error fetching comments history:", comments.reason);
      }
    },
    applyDetails(details) {
      this.details = details;
      // the request's own copy of the item, fresh after an edit
      this.equipment = details.equipment;
      this.model = details.model;
      this.registration = details.registration_number;
      this.location = details.equipmentLocation;
      this.cause = details.cause;
    },
    async reloadAfterItemEdit() {
      const requestId = this.request && this.request.requestId;
      if (!requestId) return;
      try {
        const [details, comments] = await Promise.all([
          api.getRequestDetails(requestId),
          api.getRequestCommentsHistory(requestId),
        ]);
        if (details) this.applyDetails(details);
        this.commentsHistory = comments;
      } catch (error) {
        console.error("Error reloading request:", error);
      }
    },
    onItemSaved(message) {
      this.$emit("success", message);
      this.$emit("updateRequestData");
      this.reloadAfterItemEdit();
    },
    openAddComment() {
      if (!this.request) return;
      const what = [this.cause, this.model].filter(Boolean).join(", ");
      this.$refs.addComment.open(this.request.requestId, what ? `${what}. Demersul nu își schimbă etapa.` : "");
    },
    // the comment is in the history now; the list behind shows it too
    onCommentAdded() {
      this.$emit("updateRequestData");
      this.reloadAfterItemEdit();
    },
    async record(step) {
      // a second click while the first one is still saving would log the same step twice
      if (this.saving || this.photosUploading || !this.request) return;
      if (step.id === DONE && this.priceError) {
        this.$emit("error", this.priceError);
        return;
      }
      this.saving = true;
      this.savingStep = step.id;
      try {
        // the server checks the step and moves the request (and gives the demers its number when the work starts)
        await api.saveResponseData({
          response_type_id: step.id,
          request_id: this.request.requestId,
          comments: this.body,
          file_ids: this.photos.map((photo) => photo.id),
          ...(step.id === DONE ? { service_price: String(this.price || "") } : {}),
        });
        this.$emit("success", DONE_MESSAGES[step.id] || "Demersul a fost aprobat");
        this.$emit("updateRequestData");
        this.showApprovalPopup = false;
      } catch (error) {
        console.error("Error saving response:", error);
        this.$emit("error", error.response?.data?.message || "Decizia nu a fost salvată. Încercați din nou.");
        // someone else may have moved the request meanwhile
        if (error.response && error.response.status === 409) this.$emit("updateRequestData");
        this.reloadSteps();
      } finally {
        this.saving = false;
        this.savingStep = null;
      }
    },
    async reloadSteps() {
      const token = this.loadToken;
      try {
        const steps = await api.getNextSteps(this.request.requestId);
        if (token === this.loadToken) this.steps = steps || [];
      } catch (error) {
        console.error("Error fetching next steps:", error);
      }
    },
    stepIcon(step) {
      return STEP_ICONS[step.id] || (step.tone === "reject" ? "mdi-close-circle-outline" : "mdi-check-circle-outline");
    },
    calcTimeDifference(created_time) {
      return helper.calcTimeDifference(created_time);
    },
    showConfirmCard(step) {
      this.pendingStep = step;
      this.confirmTitle = `„${step.label}”: doriți să continuați?`;
      this.confirmCard = true;
    },
    confirmAction() {
      this.confirmCard = false;
      if (this.pendingStep) {
        this.record(this.pendingStep);
      }
      this.pendingStep = null;
    },
    cancelConfirmCard() {
      this.confirmCard = false;
      this.pendingStep = null;
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
  watch: {
    request: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.body = "";
          this.photos = [];
          this.price = "";
          this.equipment = newValue.equipment;
          this.registration = newValue.registration_number;
          this.user = newValue.user;
          this.department = newValue.department;
          this.model = newValue.model;
          this.location = newValue.equipmentLocation;
          this.cause = newValue.cause;
          this.loadData();
        }
      },
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    canFinish() {
      return this.steps.some((step) => step.id === DONE);
    },
    priceError() {
      return Number.isNaN(helper.parsePrice(this.price)) ? helper.PRICE_ERROR : "";
    },
    rejectSteps() {
      return this.steps.filter((step) => step.tone === "reject");
    },
    secondarySteps() {
      return this.steps.filter((step) => step.tone !== "reject" && step.tone !== "approve");
    },
    approveSteps() {
      return this.steps.filter((step) => step.tone === "approve");
    },
    noStepsText() {
      if (this.stepsFailed) return "Acțiunile nu au putut fi încărcate. Închideți și deschideți din nou demersul.";
      if (this.getUser.role === "admin") {
        // the admins execute the demersuri of the types Configurare gives them; they never approve
        const engineerRole = (this.details && this.details.engineerRole) || (this.request && this.request.engineerRole);
        return this.$store.getters.actsAsEngineerFor(engineerRole)
          ? "Demersul nu așteaptă acum acțiunea dvs."
          : "Administratorul nu aprobă demersurile; execută doar demersurile tipurilor date administratorilor în Configurare.";
      }
      return "Demersul nu așteaptă acum decizia dvs.";
    },
    statusId() {
      if (this.details) return this.details.statusId;
      return this.request ? this.request.statusId : null;
    },
    isCorrected() {
      return this.statusId === STAGES_005.CORRECTED;
    },
    requestTypeId() {
      return this.details ? this.details.requestTypeId : null;
    },
    createsEquipment() {
      if (this.details) return !!this.details.createsEquipment;
      return !!(this.request && this.request.createsEquipment);
    },
    canEditItem() {
      return ITEM_EDITOR_ROLES.includes(this.getUser.role) && !!(this.details && this.details.actions && this.details.actions.editItem);
    },
    // whoever opens a demer may comment on it, except on a cancelled one
    canComment() {
      return !!this.request && this.statusId !== STAGES_005.CANCELLED;
    },
    hasPrevious() {
      return !!(this.execution && this.execution.id !== null && this.execution.id !== undefined);
    },
    sameCause() {
      return (this.execution && this.execution.sameCauseLastYear) || 0;
    },
    sameCauseText() {
      const cause = this.cause ? ` «${this.cause}»` : "";
      return this.sameCause === 0 ? `nicio cerere${cause}` : `${requestsCount(this.sameCause)}${cause}`;
    },
    historyCountText() {
      const n = (this.execution && this.execution.history && this.execution.history.length) || 0;
      // the server sends at most the last 10
      return n >= 10 ? "ultimele 10" : String(n);
    },
    refillAlert() {
      return this.hasPrevious && this.sameCause >= REFILL_ALERT;
    },
    execClass() {
      if (this.refillAlert) return "last-exec--alert";
      if (this.execLoading || !this.hasPrevious) return "last-exec--first";
      return "";
    },
  },
};
</script>

<style scoped>
.facts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}
.facts {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 14px;
  margin: 0 0 12px;
}
.facts dt {
  color: var(--ink-3);
}
.facts dd {
  margin: 0;
  font-weight: 600;
  word-break: break-word;
}
.notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 1.02rem;
}
.notice--sky {
  background: var(--sky-bg);
  color: var(--sky);
}
.notice--sky .v-icon {
  color: var(--sky);
}
.new-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: -4px 0 12px;
  padding: 3px 12px 3px 9px;
  border-radius: 999px;
  background: var(--plum-bg);
  color: var(--plum);
  font-size: 0.88rem;
  font-weight: 700;
}
.new-item .v-icon {
  color: var(--plum);
}
.item-edit {
  margin: 0 0 12px;
}
.item-edit__hint {
  margin: 6px 0 0;
  color: var(--ink-3);
  font-size: 0.88rem;
}
.last-exec {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--amber-bg);
  color: #6b4306;
  font-size: 1.02rem;
}
.last-exec .v-icon {
  color: var(--amber);
}
.last-exec__main {
  flex: 1;
  min-width: 0;
}
.last-exec__count {
  margin-top: 4px;
}
.last-exec__toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.last-exec__toggle span {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.last-exec__toggle .v-icon {
  color: inherit !important;
}
.last-exec__list {
  margin: -8px 0 16px;
}
.last-exec--first {
  background: var(--mint-50);
  color: var(--ink-2);
}
.last-exec--first .v-icon {
  color: var(--green);
}
/* the anti-cheat for toner recharges: many requests with the same cause on one item */
.last-exec--alert {
  background: var(--brick-bg);
  color: #7a1f1a;
  border: 2px solid var(--brick);
  font-size: 1.06rem;
}
.last-exec--alert > .v-icon {
  color: var(--brick);
  font-size: 30px;
}
.last-exec--alert .last-exec__count strong {
  padding: 1px 8px;
  border-radius: 6px;
  background: var(--brick);
  color: #fff;
}
.last-exec__warn {
  margin-top: 6px;
  font-weight: 700;
}
.comments {
  list-style: none;
  padding: 0;
  margin: 0 0 10px;
}
.comments li {
  padding: 10px 0;
}
.comments li + li {
  border-top: 1px solid var(--line-soft);
}
.comments__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
}
.comments__date {
  color: var(--ink-3);
  font-size: 0.88rem;
}
.comments__text {
  margin-top: 4px;
  color: var(--ink-2);
  white-space: pre-line;
}
.comments__photos {
  margin-top: 8px;
}
.comments__add {
  margin-top: 4px;
}
.steps-note {
  color: var(--ink-2);
  font-weight: 600;
}
.steps-note__icon.v-icon {
  color: var(--ink-3);
}
@media (max-width: 760px) {
  .facts-grid {
    grid-template-columns: 1fr;
  }
}
</style>
